import { motion } from 'framer-motion'
import { Quote, TrendingUp, Clock, Shield } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'

interface TestimonialCardProps {
  quote: string
  author: string
  company: string
  savings: string
  icon: any
  index: number
}

const TestimonialCard = ({ quote, author, company, savings, icon: Icon, index }: TestimonialCardProps) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="group"
    >
      <Card className="h-full bg-card/50 backdrop-blur-sm border-border hover:border-primary/20 transition-all duration-300 relative overflow-hidden">
        <CardContent className="p-6">
          {/* Background gradient */}
          <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-primary/10 to-transparent rounded-full transform translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-500"></div>
          
          <div className="relative z-10">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mr-3 group-hover:bg-primary/20 transition-colors">
                <Icon className="w-5 h-5 text-primary" />
              </div>
              <Quote className="w-6 h-6 text-muted-foreground" />
            </div>
            
            <blockquote className="text-foreground mb-6 text-lg font-medium leading-relaxed">
              "{quote}"
            </blockquote>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-foreground">{author}</p>
                  <p className="text-sm text-muted-foreground">{company}</p>
                </div>
              </div>
              
              <div className="pt-3 border-t border-border">
                <p className="text-sm font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full inline-block">
                  {savings}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}

export const SocialProof = () => {
  const testimonials = [
    {
      quote: "Blocked 2,400 fraudulent leads in our first month. The sequential phone detection is incredible.",
      author: "Marketing Director",
      company: "E-commerce Platform",
      savings: "$72,000/month saved",
      icon: Shield
    },
    {
      quote: "Reduced manual review time by 75%. Our team can focus on legitimate leads instead of filtering fraud.",
      author: "Operations Manager",
      company: "Lead Generation Agency",
      savings: "40 hours/week saved",
      icon: Clock
    },
    {
      quote: "Sequential phone detection caught sophisticated bot attacks that other solutions missed completely.",
      author: "Security Lead",
      company: "Financial Services",
      savings: "$150,000 fraud prevented",
      icon: TrendingUp
    }
  ]

  const stats = [
    { label: "Businesses Protected", value: "2,400+" },
    { label: "Fraudulent Leads Blocked", value: "1.2M+" },
    { label: "Money Saved", value: "$45M+" },
    { label: "Average ROI", value: "340%" }
  ]

  return (
    <div className="py-16 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Trusted by Businesses to Stop Fraud
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-12">
            Join thousands of companies protecting their revenue with our fraud detection platform
          </p>
          
          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-muted-foreground uppercase tracking-wide">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              {...testimonial}
              index={index}
            />
          ))}
        </div>
      </div>
    </div>
  )
}